import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  LinearProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Inventory2 as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import { newHiresApi, onboardingApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  NewHire,
  NewHireCreate,
  NewHireStatus,
  PositionType,
} from '../types';

const statusColors: Record<NewHireStatus, 'primary' | 'success' | 'error'> = {
  active: 'primary',
  completed: 'success',
  terminated: 'error',
};

const statusLabels: Record<NewHireStatus, string> = {
  active: 'Active',
  completed: 'Completed',
  terminated: 'Terminated',
};

const NewHires: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isManager } = useAuth();

  // State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<NewHireStatus | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Form state
  const [formData, setFormData] = useState<NewHireCreate>({
    employee_name: '',
    position: '',
    position_type_id: undefined,
    department: '',
    hire_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  // Position types query
  const { data: positionTypesData } = useQuery(
    'position-types',
    () => onboardingApi.getPositionTypes()
  );
  const positionTypes = positionTypesData?.position_types?.filter((pt: PositionType) => pt.is_active) || [];

  // Query
  const { data, isLoading } = useQuery(
    ['new-hires', page, rowsPerPage, search, statusFilter, showArchived],
    () =>
      newHiresApi.getNewHires({
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        search: search || undefined,
        status: statusFilter || undefined,
        include_archived: showArchived || undefined,
      }),
    { keepPreviousData: true }
  );

  // Stats query
  const { data: stats } = useQuery('new-hires-stats', newHiresApi.getDashboardStats);

  // Mutations
  const createMutation = useMutation(newHiresApi.createNewHire, {
    onSuccess: (newHire) => {
      queryClient.invalidateQueries(['new-hires']);
      queryClient.invalidateQueries(['new-hires-stats']);
      handleCloseDialog();
      navigate(`/new-hires/${newHire.id}`);
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create new hire');
    },
  });

  const archiveMutation = useMutation(
    ({ id, isArchived }: { id: number; isArchived: boolean }) =>
      newHiresApi.archiveNewHire(id, isArchived),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['new-hires']);
        queryClient.invalidateQueries(['new-hires-stats']);
      },
      onError: (err: any) => {
        setError(err.response?.data?.detail || 'Failed to archive new hire');
      },
    }
  );

  const deleteMutation = useMutation(newHiresApi.deleteNewHire, {
    onSuccess: () => {
      queryClient.invalidateQueries(['new-hires']);
      queryClient.invalidateQueries(['new-hires-stats']);
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to delete new hire');
    },
  });

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      employee_name: '',
      position: '',
      position_type_id: undefined,
      department: '',
      hire_date: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setError('');
  };

  const handleSubmit = () => {
    if (!formData.employee_name || !formData.position_type_id || !formData.hire_date) {
      setError('Employee name, position type, and hire date are required');
      return;
    }
    // Set position from position type name for display purposes
    const selectedPositionType = positionTypes.find((pt: PositionType) => pt.id === formData.position_type_id);
    const submitData = {
      ...formData,
      position: selectedPositionType?.name || formData.position,
    };
    createMutation.mutate(submitData);
  };

  const handleArchive = (newHire: NewHire) => {
    const action = newHire.is_archived ? 'unarchive' : 'archive';
    if (window.confirm(`Are you sure you want to ${action} ${newHire.employee_name}?`)) {
      archiveMutation.mutate({ id: newHire.id, isArchived: !newHire.is_archived });
    }
  };

  const handleDelete = (newHire: NewHire) => {
    if (window.confirm(`Are you sure you want to delete ${newHire.employee_name}?`)) {
      deleteMutation.mutate(newHire.id);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            New Hires
          </Typography>
          {isManager && (
            <Box>
              <Button
                variant="outlined"
                onClick={() => navigate('/onboarding-config')}
                sx={{ mr: 2 }}
              >
                Configure Checklist
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
              >
                Add New Hire
              </Button>
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {stats.active_new_hires}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active New Hires
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {stats.completed_onboarding}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed Onboarding
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">
                  {stats.avg_onboarding_progress.toFixed(0)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg. Progress
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">
                  {stats.recent_hires}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hired (Last 30 Days)
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search new hires..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value as NewHireStatus | '')}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  {Object.entries(statusLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showArchived}
                    onChange={(e) => { setShowArchived(e.target.checked); setPage(0); }}
                    size="small"
                  />
                }
                label="Show Archived"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <TableContainer component={Paper}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee Name</TableCell>
                    <TableCell>Position</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Hire Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ width: 200 }}>Progress</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.new_hires.map((newHire) => (
                    <TableRow key={newHire.id} hover sx={newHire.is_archived ? { opacity: 0.6 } : undefined}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                            onClick={() => navigate(`/new-hires/${newHire.id}`)}
                          >
                            {newHire.employee_name}
                          </Typography>
                          {newHire.is_archived && (
                            <Chip label="Archived" size="small" variant="outlined" color="default" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{newHire.position}</TableCell>
                      <TableCell>{newHire.department || '-'}</TableCell>
                      <TableCell>
                        {(() => {
                          const [year, month, day] = newHire.hire_date.split('-').map(Number);
                          return new Date(year, month - 1, day).toLocaleDateString('en-US');
                        })()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabels[newHire.status]}
                          color={statusColors[newHire.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={newHire.progress_percentage}
                            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                            color={newHire.progress_percentage === 100 ? 'success' : 'primary'}
                          />
                          <Typography variant="body2" sx={{ minWidth: 45 }}>
                            {newHire.progress_percentage.toFixed(0)}%
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {newHire.completed_items}/{newHire.total_items} items
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/new-hires/${newHire.id}`)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {isManager && (
                          <Tooltip title={newHire.is_archived ? 'Unarchive' : 'Archive'}>
                            <IconButton
                              size="small"
                              onClick={() => handleArchive(newHire)}
                            >
                              {newHire.is_archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {isManager && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(newHire)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {data?.new_hires.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No new hires found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={data?.total || 0}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </TableContainer>
      </Box>

      {/* Add New Hire Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Hire</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Employee Name"
                value={formData.employee_name}
                onChange={(e) => setFormData({ ...formData, employee_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Position Type</InputLabel>
                <Select
                  value={formData.position_type_id || ''}
                  label="Position Type"
                  onChange={(e) =>
                    setFormData({ ...formData, position_type_id: e.target.value as number || undefined })
                  }
                >
                  {positionTypes.map((pt: PositionType) => (
                    <MenuItem key={pt.id} value={pt.id}>
                      {pt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Hire Date"
                type="date"
                value={formData.hire_date}
                onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isLoading}
          >
            {createMutation.isLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default NewHires;
